import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Input, Typography, Toast, useInput } from '@chilecompra/react-kit/components'
import { checkInputErrorRegEx, checkInputErrorRequired, useViewport } from '@chilecompra/react-kit'
import Products from './AddProductsServices.products'
import { LETTERS_NUMBERS } from '../../modules/utils/regexes'
import { onGetProductsThunk, onAddProductSuccess } from './AddProductsServices.actions'
import { Card, Divider, Grid, LineJump, NotFoundImg, Root } from './AddProductsServices.styles'

/**
 * The AddProductsServices's container.
 */
const AddProductsServices = () => {
  const { products, productsSaved, loading, error } = useSelector(store => store.addProductsService)
  const dispatch = useDispatch()

  const {
    size: { isDownToSmall, isDownToTiny }
  } = useViewport()

  const {
    error: productsError,
    value: productsValue,
    onChange: handleProductsChange
  } = useInput({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorRegEx(LETTERS_NUMBERS)]
  })

  const handleOnSearchProduct = () => dispatch(onGetProductsThunk({ name: productsValue }))

  const handleAddProduct = (productId, productName, categoryId) => {
    const productKey = (Math.random() + 1).toString(36).substring(7)
    const newProduct = {
      productId,
      productKey,
      productName,
      categoryId,
      quantity: null,
      unit: null,
      detail: ''
    }

    dispatch(onAddProductSuccess({ newProduct }))
  }

  return (
    <Root>
      <Grid container>
        <Grid item xs={12} md={6} lg={3}>
          <Typography
            color="black1"
            fontSize="14px"
            fontWeight="bold"
            lineHeight="18px"
            margin="0 0 4px"
            variant="body2"
          >
            Texto o ID de productos a buscar
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Input
            autoFocus
            error={productsError}
            onChange={handleProductsChange}
            placeholder="Ejm. Cuaderno"
            type="text"
            value={productsValue}
          />
          <Typography color="gray1" fontSize="12px" lineHeight="14px" variant="subtitle">
            Para refinar tu búsqueda puedes utilizar los siguientes comandos: <LineJump />
            - 'Texto': Búsqueda exacta <LineJump />
            - Texto*: Palabras que comienzan con <LineJump />- *texto*: Palabras que contienen
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg container justifyContent={isDownToSmall ? 'flex-start' : 'flex-end'}>
          <Button
            color="primary"
            disabled={productsError !== '' || loading}
            loading={loading}
            margin={isDownToSmall ? '10px 0 0' : '0'}
            onClick={handleOnSearchProduct}
            type="button"
            variant="outlined"
            width={isDownToTiny ? '100%' : undefined}
          >
            Buscar
          </Button>
        </Grid>
      </Grid>
      {!loading && error && error?.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
        <Grid container margin="16px 0 24px">
          <Grid item xs={12}>
            <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
              En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
              minutos más.
            </Toast>
          </Grid>
        </Grid>
      )}
      {!loading && error && error?.reason === 'NOT_FOUND_ERROR' && (
        <Grid container margin="16px 0 24px">
          <Grid item xs={12}>
            <Card color="blank" padding="75.5px 0 74.5px" textAlign="center">
              <NotFoundImg src="/assets/images/nofound.svg" />
              <Typography fontWeight="bold" margin="8px 0" variant="h4">
                No se encontraron resultados para tu búsqueda
              </Typography>
              <Typography color="black3" fontSize="14px" lineHeight="18px" variant="body1">
                El producto o servicio que buscas no se encuentra en
                <br />
                Mercado Público. Intenta con otra palabra clave u
                <br />
                otro criterio de búsqueda.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )}
      {products && (
        <>
          <LineJump />
          {products.map((category, i) => (
            <Grid key={category.name}>
              <Grid item sm={12}>
                <Typography color="black1" fontWeight="bold" key={category.name} variant="body2">
                  {category.name}
                </Typography>
              </Grid>
              {category.products.map(product => (
                <Products
                  categoryId={product.categoryId}
                  onAddProduct={handleAddProduct}
                  key={product.id}
                  isAdded={productsSaved.some(added => added.productId === product.id)}
                  productId={product.id}
                  productName={product.name}
                />
              ))}
              {products.length - 1 !== i && isDownToTiny && <Divider />}
              <LineJump />
            </Grid>
          ))}
        </>
      )}
    </Root>
  )
}

export default AddProductsServices
